import React, { useState } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  FormFeedback,
  FormGroup,
  Row,
  Col,
} from "reactstrap"
import { useFormik } from "formik"
import * as Yup from "yup"
import FormLabel from "components/Extra/FormLabel"
import { FormInput } from "components/Extra/FormInput"
import { useTranslation } from "react-i18next"

interface FilterByIdRangeModalProps {
  onIdRangeChange?: (startId: string, endId: string) => void
  className?: string
  style?: React.CSSProperties
}

export const FilterByIdRangeModal: React.FC<FilterByIdRangeModalProps> = ({
  onIdRangeChange,
  className,
  style
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [displayIds, setDisplayIds] = useState({
    startId: "1",
    endId: "100"
  })
  const { t } = useTranslation()

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      startId: "",
      endId: "",
    },
    validationSchema: Yup.object({
      startId: Yup.number()
        .typeError(t("revenue_startIdRequired") as string)
        .required(t("revenue_startIdRequired") as string)
        .positive(t("revenue_startIdRequired") as string)
        .integer(t("revenue_startIdRequired") as string)
        .test(
          'is-less-or-equal',
          t("revenue_invalidIdRange") as string,
          function(value) {
            const { endId } = this.parent;
            return !endId || Number(value) <= Number(endId);
          }
        ),
      endId: Yup.number()
        .typeError(t("revenue_endIdRequired") as string)
        .required(t("revenue_endIdRequired") as string)
        .positive(t("revenue_endIdRequired") as string)
        .integer(t("revenue_endIdRequired") as string)
        .test(
          'is-greater-or-equal',
          t("revenue_invalidIdRange") as string,
          function(value) {
            const { startId } = this.parent;
            return !startId || Number(value) >= Number(startId);
          }
        ),
    }),
    onSubmit: values => {
      if (onIdRangeChange) {
        onIdRangeChange(values.startId, values.endId)
      }
      setDisplayIds({
        startId: values.startId,
        endId: values.endId
      })
      toggle()
    },
  })

  const toggle = () => {
    setIsOpen(!isOpen)
  }

  const formatIdDisplay = (id: string) => {
    return `ID-${id}`
  }

  const startIdDisplay = formatIdDisplay(displayIds.startId)
  const endIdDisplay = formatIdDisplay(displayIds.endId)

  return (
    <div
      style={{ flex: 1, ...style }}
      className={`d-flex justify-content-end ${className}`}
    >
      <span
        style={{
          cursor: "pointer",
        }}
        onClick={toggle}
      >
        {startIdDisplay} to {endIdDisplay} <i className="fas fa-angle-down"></i>
      </span>

      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle} tag="h3">
          <h4 className="text-capitalize fw-semibold font-size-24">
            {t("revenue_idRange")}
          </h4>
        </ModalHeader>
        <ModalBody>
          <Form
            onSubmit={e => {
              e.preventDefault()
              validation.handleSubmit()
              return false
            }}
          >
            <Row>
              <Col md="6">
                <FormInput
                  label={t("revenue_startId") as string}
                  name="startId"
                  type="number"
                  placeholder="1"
                  validation={validation}
                  required
                />
              </Col>
              <Col md="6">
                <FormInput
                  label={t("revenue_endId") as string}
                  name="endId"
                  type="number"
                  placeholder="100"
                  validation={validation}
                  required
                />
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="submit"
                className={`btn bg-gradiant-blue text-white border-0 save-customer`}
                disabled={!validation.isValid}
              >
                {t("common_submit")}
              </button>
              <button
                onClick={toggle}
                type="button"
                className="btn btn-cancel text-primary border border-primary"
              >
                {t("common_cancel")}
              </button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default FilterByIdRangeModal